import { useCallback, useState } from 'react';
import { useApi } from 'api/api-context';
import { Stack } from '@mantine/core';
import FormFooter from 'components/forms/FormFooter';
import { useTemplateEditorData } from 'pages/revisions-module/template-editor/TemplateEditorDataProvider';
import { showNotification } from '@mantine/notifications';
import { SUCCESS_NOTIFICATION_COLOR } from 'utils/constants';
import panic from 'errors/panic';
import { useFormInputs } from 'pages/revisions-module/template-editor/editors/form/input/FormInputsDataProvider';
import { useFormSections } from 'pages/revisions-module/template-editor/editors/form/section/FormSectionsDataProvider';
import { ITemplate } from 'pages/revisions-module/template-editor/editors/template/types';
import { IForm } from 'pages/revisions-module/template-editor/editors/form/types';
import { IVisual } from 'pages/revisions-module/template-editor/editors/visual/types';
import { IPipeline } from 'pages/revisions-module/template-editor/editors/pipeline/types';
import getJsonAttribute from 'utils/get-json-attribute';
import {
  DEFAULT_DEVICE_MEASUREMENTS_PLACEHOLDER_FORMAT,
  DEVICE_MEASUREMENTS_INDEX_COLUMN_KEY,
  DeviceMeasurementsPlaceholderFormat,
  DeviceMeasurementsPlaceholderWidth,
} from 'pages/revisions-module/template-editor/editors/template/extensions/placeholder/device-measurements/DeviceMeasurementsPlaceholder';
import { getDeviceMeasurementsPlaceholderColumns } from 'pages/revisions-module/template-editor/editors/template/extensions/placeholder/device-measurements/device-measurements-placeholder-columns';

/**
 * Converts a persisted measurements width config into a CSS width value.
 */
function normalizeDeviceMeasurementsWidth(width?: DeviceMeasurementsPlaceholderWidth) {
  if (!width || width.unit === 'auto') {
    return 'auto';
  }

  if (Number.isInteger(width.value) && (width.value ?? 0) > 0) {
    return `${width.value}${width.unit}`;
  }

  return 'auto';
}

/**
 * Preserves an explicitly saved heading, including an empty string override.
 */
function resolveDeviceMeasurementsHeading(savedHeading: string | undefined, fallbackHeading: string) {
  return savedHeading === undefined ? fallbackHeading : savedHeading;
}

/**
 * Wrapper which provides save functionality to the form editor.
 */
export default function TemplateEditorFormWrapper({ children }: { children: React.ReactNode }) {
  const { getAction } = useApi();
  const { revisionTemplateId, form, template, visual, pipeline } = useTemplateEditorData();
  const { getInputByName, allInputs } = useFormInputs();
  const { getSectionById } = useFormSections();
  const [loading, setLoading] = useState(false);

  const postprocessForm = useCallback((): IForm => form, [form]);

  const postprocessTemplate = useCallback((): ITemplate => {
    const eTemplate = document.createElement('div');
    eTemplate.innerHTML = template.template;

    // Set the format for number and sum placeholders.
    const placeholders = eTemplate.querySelectorAll('.placeholder[data-type="number"], .placeholder[data-type="sum"]');
    placeholders.forEach((placeholder) => {
      const inputName = placeholder.getAttribute('data-name') ?? '';
      const input = getInputByName(inputName);

      if (input && (input.spec.type === 'number' || input.spec.type === 'sum')) {
        const { precision = 0 } = input.spec;
        placeholder.setAttribute('data-format', `t{,${precision}}`);
      }
    });

    // Set the format for deviceMeasurement
    const deviceMeasurements = eTemplate.querySelectorAll('.device-measurements-placeholder');
    deviceMeasurements.forEach((deviceMeasurement) => {
      const sectionId = deviceMeasurement.getAttribute('data-section-id') ?? '';
      const section = getSectionById(sectionId);
      const format = getJsonAttribute<DeviceMeasurementsPlaceholderFormat>(
        deviceMeasurement as HTMLElement,
        'data-format',
        DEFAULT_DEVICE_MEASUREMENTS_PLACEHOLDER_FORMAT
      );

      if (section) {
        const columnsByKey = new Map((format.columns ?? []).map((column) => [column.key, column] as const));
        const columns = getDeviceMeasurementsPlaceholderColumns(section).map((column) => ({
          ...column,
          label: resolveDeviceMeasurementsHeading(columnsByKey.get(column.key)?.label, column.label),
          show: columnsByKey.get(column.key)?.show ?? true,
          width: normalizeDeviceMeasurementsWidth(format.widths.find((width) => width.key === column.key)),
        }));
        const savedIndexColumn = format.indexColumn;

        const mergedFormat: DeviceMeasurementsPlaceholderFormat = {
          ...DEFAULT_DEVICE_MEASUREMENTS_PLACEHOLDER_FORMAT,
          ...format,
          columns,
          indexColumn: {
            key: DEVICE_MEASUREMENTS_INDEX_COLUMN_KEY,
            label: resolveDeviceMeasurementsHeading(savedIndexColumn?.label, 'Číslo'),
            show: savedIndexColumn?.show ?? true,
            width: normalizeDeviceMeasurementsWidth(
              format.widths.find((width) => width.key === DEVICE_MEASUREMENTS_INDEX_COLUMN_KEY)
            ),
          },
        };

        deviceMeasurement.setAttribute('data-format', JSON.stringify(mergedFormat));
      }
    });

    return { template: eTemplate.innerHTML };
  }, [form, template]);

  const postprocessVisual = useCallback((): IVisual => visual, [visual]);

  const postprocessPipeline = useCallback((): IPipeline => pipeline, [pipeline]);

  /**
   * Submits the formScheme, templateScheme and visualData.
   */
  const submit = () => {
    const configureRevisionTemplate = getAction('RevisionTemplateConfigure');

    setLoading(true);

    const form = postprocessForm();
    const template = postprocessTemplate();
    const visual = postprocessVisual();
    const pipeline = postprocessPipeline();

    const indexes = allInputs
      .filter((input) => input.spec.index)
      .map((input) => ({
        fieldName: input.spec.name,
        fieldType: input.spec.type,
        fieldLabel: input.spec.label,
        context: input.context,
      }));

    configureRevisionTemplate({
      parameters: { revisionTemplateId },
      payload: {
        formScheme: JSON.stringify(form),
        templateScheme: JSON.stringify(template),
        visualData: JSON.stringify(visual),
        pipelineScheme: JSON.stringify(pipeline),
        indexes,
      },
    })
      .then(() => {
        showNotification({
          title: 'Šablóna bola úspešne uložená',
          message: '',
          color: SUCCESS_NOTIFICATION_COLOR,
        });
      })
      .catch(panic)
      .finally(() => setLoading(false));
  };

  return (
    <Stack spacing={0}>
      {children}
      <FormFooter onPrimaryClick={submit} loading={loading} />
    </Stack>
  );
}
